/**
 * useWorkspace — Workspace 管理 Hook
 * @ref: V1.1 前端实现
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/apiClient';
import { message } from 'antd';
import { expectArrayPayload } from '@/api/strictQueryPayload';

// ---- Types ----

export type MemberRole = 'VIEWER' | 'OPERATOR' | 'MANAGER' | 'ADMIN';

export interface Workspace {
  workspaceId: number;
  workspaceName: string;
  description?: string;
  memberCount: number;
  currentUserRole?: MemberRole;
  createdAt: string;
  updatedAt: string;
}

export interface WorkspaceMember {
  userId: string;
  username: string;
  email?: string;
  role: MemberRole;
  joinedAt: string;
}

export interface CreateWorkspaceDTO {
  name: string;
  description?: string;
}

export interface UpdateMemberRoleDTO {
  userId: string;
  role: MemberRole;
}

// ---- API Functions ----

// 后端统一返回 { code, message, data }，统一解包
const unwrap = <T>(res: any): T => res?.data ?? res;

const getWorkspaces = async (): Promise<Workspace[]> => {
  const res = await apiClient.get('/workspaces');
  return expectArrayPayload<Workspace>(unwrap<unknown>(res), 'workspaces');
};

const createWorkspace = async (data: CreateWorkspaceDTO): Promise<Workspace> => {
  const res = await apiClient.post('/workspaces', data);
  return unwrap<Workspace>(res);
};

const getMembers = async (workspaceId: number): Promise<WorkspaceMember[]> => {
  const res = await apiClient.get(`/workspaces/${workspaceId}/members`);
  return expectArrayPayload<WorkspaceMember>(unwrap<unknown>(res), 'workspace members');
};

const addMember = async (
  workspaceId: number,
  email: string,
  role: MemberRole
): Promise<WorkspaceMember> => {
  const res = await apiClient.post(`/workspaces/${workspaceId}/members`, { email, role });
  return unwrap<WorkspaceMember>(res);
};

const removeMember = async (workspaceId: number, userId: string): Promise<void> => {
  return await apiClient.delete(`/workspaces/${workspaceId}/members/${userId}`);
};

const updateMemberRole = async (
  workspaceId: number,
  userId: string,
  role: MemberRole
): Promise<void> => {
  return await apiClient.put(`/workspaces/${workspaceId}/members/${userId}`, { role });
};

// ---- Hooks ----

export const useWorkspaceList = () => {
  return useQuery<Workspace[], Error>({
    queryKey: ['workspaces'],
    queryFn: getWorkspaces,
    staleTime: 60_000,
  });
};

export const useCreateWorkspace = () => {
  const qc = useQueryClient();
  return useMutation<Workspace, Error, CreateWorkspaceDTO>({
    mutationFn: createWorkspace,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaces'] });
      message.success('工作区创建成功');
    },
    onError: (e: Error) => message.error(e.message || '创建失败'),
  });
};

export const useWorkspaceMembers = (workspaceId: number) => {
  return useQuery<WorkspaceMember[], Error>({
    queryKey: ['workspaceMembers', workspaceId],
    queryFn: () => getMembers(workspaceId),
    enabled: !!workspaceId,
  });
};

export const useAddMember = (workspaceId: number) => {
  const qc = useQueryClient();
  return useMutation<WorkspaceMember, Error, { email: string; role: MemberRole }>({
    mutationFn: vars => addMember(workspaceId, vars.email, vars.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaceMembers', workspaceId] });
      message.success('成员添加成功');
    },
    onError: (e: Error) => message.error(e.message || '添加失败'),
  });
};

export const useRemoveMember = (workspaceId: number) => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: userId => removeMember(workspaceId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaceMembers', workspaceId] });
      message.success('成员已移除');
    },
    onError: (e: Error) => message.error(e.message || '移除失败'),
  });
};

export const useUpdateMemberRole = (workspaceId: number) => {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: string; role: MemberRole }>({
    mutationFn: vars => updateMemberRole(workspaceId, vars.userId, vars.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workspaceMembers', workspaceId] });
      message.success('角色更新成功');
    },
    onError: (e: Error) => message.error(e.message || '更新失败'),
  });
};

export const ROLE_OPTIONS = [
  { value: 'VIEWER', label: '查看者 (VIEWER)' },
  { value: 'OPERATOR', label: '操作者 (OPERATOR)' },
  { value: 'MANAGER', label: '管理者 (MANAGER)' },
  { value: 'ADMIN', label: '管理员 (ADMIN)' },
];

export const ROLE_COLOR: Record<MemberRole, string> = {
  VIEWER: 'default',
  OPERATOR: 'blue',
  MANAGER: 'orange',
  ADMIN: 'red',
};

export function roleLabel(role: MemberRole | string): string {
  return ROLE_OPTIONS.find(option => option.value === role)?.label ?? role;
}
